import { TEnableReportAccessUI, TGetNotSyncedHubspotCountUI, THubspotIntegrationUI, TNoteDB, TNoteUI, TUser } from 'data/types/User';
import { authSplitApi } from '../helpers/slice.helpers';
import { EAdminUrl } from 'data/urls/admin.url';
import { TPropertyManagementDetails } from 'data/types/PropertyManagement.types';
import { enableReportAccessConverter, hubspotEnableConverter } from 'data/convertors/User.convertors';
import { TUpdatePaymentMethod, TUpdateTenantPaymentMethod } from 'data/types/Payment.types';

const URL = 'user';

export const userApi = authSplitApi('authUser', ['User', 'propertyManagement', 'users', 'Members']).injectEndpoints({
  endpoints: build => ({
    signInUser: build.query<TUser, null>({
      query() {
        return {
          url: `${URL}/signIn`,
          method: 'GET',
        };
      },
      providesTags: ['User'],
      keepUnusedDataFor: 60 * 60 * 24,
      transformResponse: (data: TUser) => ({ ...data, id: `${data.id}` }),
    }),
    updateProfile: build.mutation<TUser, TUser>({
      query({ id, ...body }) {
        return {
          url: `${URL}/${id}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Members' },
        { type: 'Members', id: arg.id },
      ],
      transformResponse: (data: TUser) => ({ ...data, id: `${data.id}` }),
    }),
    disableHubspotIntegration: build.mutation<void, {propertyManagerId: string}>({
      query(body) {
        return {
          url: `${EAdminUrl.PROPERTY_MANAGEMENT}/${EAdminUrl.HUB_SPOT}/${EAdminUrl.DISABLE}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['users', 'propertyManagement'],
    }),
    getPropertyManagementDetails: build.query<TPropertyManagementDetails, { propertyManagementId: string }>({
      query({ propertyManagementId }) {
        return {
          url: `${EAdminUrl.PROPERTY_MANAGEMENT}/${propertyManagementId}`,
          method: 'GET',
        };
      },
      providesTags: ['propertyManagement'],
    }),
    updatePlan: build.mutation<void, { propertyManagementId: string; plan: string }>({
      query(body) {
        return {
          url: `${EAdminUrl.PROPERTY_MANAGEMENT}/${EAdminUrl.PLAN}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['propertyManagement'],
    }),
    updateAccesstoCourse: build.mutation<void, { propertyManagementId: string; hasAccessToCourses: boolean }>({
      query(body) {
        return {
          url: `${EAdminUrl.PROPERTY_MANAGEMENT}/${EAdminUrl.COURSES}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['propertyManagement'],
    }),
    updateShouldTenantSign: build.mutation<void, { propertyManagementId: string; shouldSign: boolean }>({
      query(body) {
        return {
          url: `${EAdminUrl.PROPERTY_MANAGEMENT}/${EAdminUrl.SHOULD_TENANT_SIGN}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['propertyManagement'],
    }),
    enableReportAccess: build.mutation<void, TEnableReportAccessUI>({
      query(params) {
        return {
          url: `${EAdminUrl.PROPERTY_MANAGEMENT}/${EAdminUrl.REPORTS}`,
          method: 'PATCH',
          body: enableReportAccessConverter.toDb(params),
        };
      },  
      invalidatesTags: ['propertyManagement'],
    }),
    enableHubspotIntegration: build.mutation<void, THubspotIntegrationUI>({
      query(body) {
        return {
          url: `${EAdminUrl.PROPERTY_MANAGEMENT}/${EAdminUrl.HUB_SPOT}/${EAdminUrl.ENABLE}`,
          method: 'PATCH',
          body: hubspotEnableConverter.toDb(body),
        };
      },
      invalidatesTags: ['users', 'propertyManagement'],
    }),
    getNotSyncedCountHubspot: build.query<TGetNotSyncedHubspotCountUI, { propertyManagerId: string }>({
      query({ propertyManagerId }) {
        return {
          url: `${EAdminUrl.PROPERTY_MANAGEMENT}/${EAdminUrl.HUB_SPOT}/${EAdminUrl.NOT_SYNCED_ANIMALS_COUNT}/${propertyManagerId}`,
          method: 'GET',
        };
      },
    }),
    updateTenantPaymentMethod: build.mutation<void, { data: TUpdateTenantPaymentMethod }>({
      query: ({ data }) => ({
        url: `${EAdminUrl.PROPERTY_MANAGEMENT}/tenant-payment-configuration`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['propertyManagement'],
    }),
    updatePaymentMethod: build.mutation<void, { data: TUpdatePaymentMethod }>({
          query: ({ data }) => ({
            url: `${EAdminUrl.PROPERTY_MANAGEMENT}/payment-configuration`,
            method: 'PUT',
            body: data,
          }),
          invalidatesTags: ['propertyManagement'],
    }),
    editNote: build.mutation<TNoteDB, TNoteUI>({
      query(body) {
        return {
          url: `${EAdminUrl.NOTES}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['users', 'propertyManagement'],
    }),
    deleteNote: build.mutation<TNoteDB, TNoteUI>({
      query(body) {
        return {
          url: `${EAdminUrl.NOTES}`,
          method: 'DELETE',
          body,
        };
      },
      invalidatesTags: ['users', 'propertyManagement'],
    }),
    addNote: build.mutation<TNoteDB, TNoteUI>({
      query(body) {
        return {
          url: `${EAdminUrl.NOTES}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['users', 'propertyManagement'],
    }),
  }),
  overrideExisting: false,
});

export const { 
  useSignInUserQuery, 
  useUpdateProfileMutation, 
  useDisableHubspotIntegrationMutation,
  useGetPropertyManagementDetailsQuery,
  useUpdatePlanMutation,
  useUpdateAccesstoCourseMutation,
  useUpdateShouldTenantSignMutation,
  useEnableReportAccessMutation,
  useEnableHubspotIntegrationMutation,
  useLazyGetNotSyncedCountHubspotQuery,
  useUpdateTenantPaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useEditNoteMutation,
  useDeleteNoteMutation,
  useAddNoteMutation,
} = userApi;
